// frontend-react/src/components/BoxScore/BoxScore.js
// Main box score container component - orchestrates all sub-components and API calls

import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, Users } from 'lucide-react';
import TeamPageHeader from '../common/TeamPageHeader';
import { dynastyTheme } from '../../services/colorService';
import { leaguesAPI } from '../../services/apiService';
import {
  fetchBoxScore,
  fetchAvailableDates,
  fetchBoxScoreRankings,
} from '../../services/boxScoreApi';
import DateNavigation from './DateNavigation';
import BoxScoreBatting from './BoxScoreBatting';
import BoxScorePitching from './BoxScorePitching';
import BoxScoreSummary from './BoxScoreSummary';

/**
 * BoxScore Component
 * Main container for box score display
 * Fetches and manages box score data and coordinates sub-components
 *
 * @param {string} leagueId - League UUID
 * @param {string} teamId - Team UUID (initial/default team)
 * @param {string} initialDate - Initial date to display (default: yesterday)
 * @param {object} bannerConfig - Banner configuration from league settings
 * @param {object} userTeam - User's team info (for logo fallback)
 */
const BoxScore = ({ leagueId, teamId, initialDate = null, bannerConfig, userTeam }) => {
  // State management
  const [currentDate, setCurrentDate] = useState(initialDate || null);
  const [boxScoreData, setBoxScoreData] = useState(null);
  const [boxScoreRankings, setBoxScoreRankings] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [datesLoading, setDatesLoading] = useState(true);
  const [leagueSettings, setLeagueSettings] = useState(null);
  
  // Team selection state
  const [allTeams, setAllTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(teamId);
  const [userTeamId, setUserTeamId] = useState(teamId);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [viewedTeamInfo, setViewedTeamInfo] = useState(null);

  // Load league settings on mount
  useEffect(() => {
    if (leagueId) {
      loadLeagueSettings();
    }
  }, [leagueId]);

  // Load teams on mount
  useEffect(() => {
    if (leagueId) {
      loadAllTeams();
    }
  }, [leagueId]);

  // Load available dates when team changes
  useEffect(() => {
    if (leagueId && selectedTeamId) {
      loadAvailableDates();
    }
  }, [leagueId, selectedTeamId]);

  // Load box score data when date or team changes
  useEffect(() => {
    if (leagueId && selectedTeamId && currentDate) {
      loadBoxScore();
    }
  }, [leagueId, selectedTeamId, currentDate]);

  /**
   * Load league settings to get scoring categories
   */
  const loadLeagueSettings = async () => {
    try {
      const response = await leaguesAPI.getLeagueSettings(leagueId);
      if (response.success && response.settings) {
        setLeagueSettings(response.settings);
      }
    } catch (err) {
      console.error('Failed to load league settings:', err);
    }
  };

  /**
   * Load all teams from API
   */
  const loadAllTeams = async () => {
    try {
      setTeamsLoading(true);
      const response = await leaguesAPI.getLeagueTeams(leagueId);

      if (response.success && response.teams) {
        const teamList = response.teams.map(team => ({
          team_id: team.team_id,
          team_name: team.team_name || 'Unnamed Team',
          manager_name: team.manager_name || 'Unknown Manager',
          is_user_team: team.is_user_team || false,
          team_logo_url: team.team_logo_url || null,
          team_colors: team.team_colors || null,
          team_motto: team.team_motto || null
        }));

        setAllTeams(teamList);

        // Find user's team
        const foundUserTeam = teamList.find(t => t.is_user_team);
        if (foundUserTeam) {
          setUserTeamId(foundUserTeam.team_id);
          // If no team selected yet, default to user's team
          if (!selectedTeamId) {
            setSelectedTeamId(foundUserTeam.team_id);
          }
        }
      }
    } catch (err) {
      console.error('Error loading teams:', err);
    } finally {
      setTeamsLoading(false);
    }
  };

  /**
   * Load available dates from API
   */
  const loadAvailableDates = async () => {
    try {
      setDatesLoading(true);
      // Don't pass season_year — let the backend use the league's current_season
      const response = await fetchAvailableDates(leagueId, selectedTeamId);
      
      if (response.success && response.dates) {
        // Sort dates in descending order (most recent first)
        const sortedDates = [...response.dates].sort((a, b) => b.localeCompare(a));
        setAvailableDates(sortedDates);
        
        // If no initial date and dates are available, use the most recent
        if (!initialDate && sortedDates.length > 0) {
          setCurrentDate(sortedDates[0]);
        }
      } else {
        console.warn('No dates available in response');
        setAvailableDates([]);
      }
    } catch (err) {
      console.error('Failed to load available dates:', err);
      setAvailableDates([]);
    } finally {
      setDatesLoading(false);
    }
  };

  /**
   * Load box score data for current date
   */
  const loadBoxScore = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`Loading box score for ${currentDate}...`);
      const response = await fetchBoxScore(leagueId, selectedTeamId, currentDate);
      
      if (response.success && response.box_score) {
        setBoxScoreData(response.box_score);
        
        // Fetch rankings for this date
        try {
          console.log('🏆 Fetching box score rankings...');
          const rankingsResponse = await fetchBoxScoreRankings(leagueId, selectedTeamId, currentDate);
          console.log('🔍 Rankings API Response:', rankingsResponse);

          if (rankingsResponse.success && rankingsResponse.your_ranks) {
            console.log('✅ Setting rankings (full response with all_rankings)');
            // Store the full response (includes your_ranks AND all_rankings)
            setBoxScoreRankings(rankingsResponse);
          } else {
            console.log('⚠️ No rankings available:', rankingsResponse.message);
            setBoxScoreRankings(null);
          }
        } catch (rankingsError) {
          console.error('❌ Failed to fetch rankings:', rankingsError);
          setBoxScoreRankings(null);
        }
      } else {
        // Use API message if available, otherwise use generic message
        const errorMsg = response.message || 'No box score data available for this date';
        setError(errorMsg);
        setBoxScoreData(null);
        setBoxScoreRankings(null);
      }
    } catch (err) {
      console.error('Failed to load box score:', err);
      setError(err.message || 'Failed to load box score data');
      setBoxScoreData(null);
      setBoxScoreRankings(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle team selection change
   */
  const handleTeamChange = (e) => {
    const newTeamId = e.target.value;
    setSelectedTeamId(newTeamId);
  };

  /**
   * Handle date change from navigation
   */
  const handleDateChange = (newDate) => {
    console.log(`Date changed to: ${newDate}`);
    setCurrentDate(newDate);
  };

  /**
   * Retry loading box score
   */
  const handleRetry = () => {
    loadBoxScore();
  };

  // Update viewedTeamInfo when team selection changes
  useEffect(() => {
    if (allTeams.length > 0) {
      const targetTeamId = selectedTeamId || userTeamId;
      const team = allTeams.find(t => t.team_id === targetTeamId);
      if (team) {
        setViewedTeamInfo(team);
      }
    }
  }, [selectedTeamId, userTeamId, allTeams]);

  // Get the current team's display info (pick ONE team object, then read fields)
  const activeTeamData = viewedTeamInfo || userTeam;
  const displayTeamName = viewedTeamInfo?.team_name || 'Team';
  const logoUrl = activeTeamData?.team_logo_url;
  const teamColors = activeTeamData?.team_colors;
  const teamMotto = activeTeamData?.team_motto;

  // Show loading state while loading dates
  if (datesLoading) {
    return (
      <div className="space-y-6">
        <div className={`${dynastyTheme.components.card.base} p-8 text-center`}>
          <div className="flex flex-col items-center gap-4">
            <div 
              className={`w-12 h-12 border-4 border-t-transparent animate-spin rounded-full ${dynastyTheme.classes.border.primary}`}
            />
            <div className={dynastyTheme.classes.text.white}>
              Loading available dates...
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error if no dates available
  if (availableDates.length === 0 && !datesLoading) {
    return (
      <div className={`${dynastyTheme.components.card.base} p-8 text-center`}>
        <AlertCircle className={`w-16 h-16 mx-auto mb-4 ${dynastyTheme.classes.text.neutralLight}`} />
        <h3 className={`${dynastyTheme.components.heading.h3} mb-2`}>
          No Box Scores Available
        </h3>
        <p className={dynastyTheme.classes.text.neutralLight}>
          No box score data has been generated for your team yet.
        </p>
        <p className={`${dynastyTheme.classes.text.neutralLight} text-sm mt-2`}>
          Box scores will be available after games are played.
        </p>
        <button
          onClick={loadAvailableDates}
          className={`mt-4 ${dynastyTheme.utils.getComponent('button', 'secondary', 'md')}`}
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <TeamPageHeader
        title={`${displayTeamName} Box Scores`}
        logoUrl={logoUrl}
        teamName={displayTeamName}
        teamColors={teamColors}
        teamMotto={teamMotto}
        bannerConfig={bannerConfig}
        badges={false}
        actions={
          <DateNavigation
            currentDate={currentDate}
            availableDates={availableDates}
            onDateChange={handleDateChange}
            loading={loading}
          />
        }
      />

      {/* Info/Error State */}
      {error && (
        <div className={`${dynastyTheme.components.card.base} p-6`}>
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className={`w-6 h-6 ${dynastyTheme.classes.text.neutralLight}`} />
            <h3 className={`${dynastyTheme.components.heading.h3}`}>
              No Box Score Available
            </h3>
          </div>
          <p className={dynastyTheme.classes.text.neutralLight}>
            {error}
          </p>
          <button
            onClick={handleRetry}
            className={`mt-4 ${dynastyTheme.utils.getComponent('button', 'secondary', 'md')}`}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      )}

      {/* Box Score Content */}
      {!error && (
        <>
          {/* Summary Stats */}
          <BoxScoreSummary
            summaryStats={boxScoreData?.summary_stats}
            teamTotals={boxScoreData?.team_totals}
            scoringCategories={leagueSettings?.scoring_categories}
            rankingsData={boxScoreRankings}
            yourTeamId={selectedTeamId}
            loading={loading}
            allTeams={allTeams}
            selectedTeamId={selectedTeamId}
            onTeamChange={handleTeamChange}
            teamsLoading={teamsLoading}
          />

          {/* Batting Stats */}
          <BoxScoreBatting
            hitting={boxScoreData?.hitting || []}
            teamTotals={boxScoreData?.team_totals}
            leagueId={leagueId}
            teamId={selectedTeamId}
            loading={loading}
          />

          {/* Pitching Stats */}
          <BoxScorePitching
            pitchers={boxScoreData?.pitching || []}
            teamTotals={boxScoreData?.team_totals}
            leagueId={leagueId}
            teamId={selectedTeamId}
            loading={loading}
          />
        </>
      )}
    </div>
  );
};

export default BoxScore;
